import { http, HttpResponse } from 'msw';
import { mockUsers } from '../fixtures/user';
import { rolesStore } from './system';

export const authHandlers = [
  // Login Handler
  http.post('/api/auth/login', async ({ request }) => {
    const body = (await request.json()) as { username?: string; password?: string };
    const { username, password } = body;

    if (!username || !password) {
      return HttpResponse.json(
        {
          code: 400,
          message: '用户名与密码不能为空',
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 400 },
      );
    }

    let matchedUser = mockUsers[username];
    if (!matchedUser) {
      // Find by username match
      const key = Object.keys(mockUsers).find((k) => mockUsers[k]?.user.username === username);
      if (key && mockUsers[key]) {
        matchedUser = mockUsers[key];
      }
    }

    if (!matchedUser || matchedUser.password !== password) {
      return HttpResponse.json(
        {
          code: 401,
          message: '用户名或密码错误 (默认密码: Password@2026)',
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 401 },
      );
    }

    // 动态从角色矩阵 rolesStore 读取最新 allowedMenus
    const matchedRole = rolesStore.find((r) => r.role === matchedUser.user.role);
    const dynamicPermissions = matchedRole?.allowedMenus
      ? (Array.isArray(matchedRole.allowedMenus) ? matchedRole.allowedMenus : JSON.parse(matchedRole.allowedMenus as any))
      : (matchedUser.user.role === 'admin' ? ['*'] : matchedUser.user.permissions);

    return HttpResponse.json({
      code: 200,
      message: '登录成功',
      success: true,
      data: {
        token: `mock_jwt_token_${matchedUser.user.role}_${matchedUser.user.id}_${Date.now()}`,
        expiresIn: 86400,
        user: {
          ...matchedUser.user,
          permissions: dynamicPermissions,
        },
      },
      timestamp: new Date().toISOString(),
    });
  }),

  // Get Current User Info
  http.get('/api/auth/user-info', ({ request }) => {
    const authHeader = request.headers.get('Authorization');
    if (!authHeader) {
      return HttpResponse.json(
        {
          code: 401,
          message: '未登录或Token已失效',
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 401 },
      );
    }

    // Decode role and userId from token format: mock_jwt_token_{role}_{userId}_{timestamp}
    const token = authHeader.replace('Bearer ', '');
    const tokenParts = token.split('_');
    // token format: mock_jwt_token_{role}_{userId}_{timestamp}
    const tokenRole = tokenParts.length >= 5 ? tokenParts[3] : undefined;
    const tokenUserId = tokenParts.length >= 6 ? tokenParts[4] : undefined;

    // Look up user by userId first, then by role, fallback to admin
    let currentUser = mockUsers.admin?.user;
    if (tokenUserId) {
      const foundEntry = Object.values(mockUsers).find((entry) => entry?.user?.id === tokenUserId);
      if (foundEntry?.user) {
        currentUser = foundEntry.user;
      }
    } else if (tokenRole) {
      const roleEntry = mockUsers[tokenRole];
      if (roleEntry?.user) {
        currentUser = roleEntry.user;
      }
    }

    const matchedRole = currentUser ? rolesStore.find((r) => r.role === currentUser.role) : null;
    const dynamicPermissions = matchedRole?.allowedMenus
      ? (Array.isArray(matchedRole.allowedMenus) ? matchedRole.allowedMenus : JSON.parse(matchedRole.allowedMenus as any))
      : (currentUser?.role === 'admin' ? ['*'] : currentUser?.permissions || []);

    return HttpResponse.json({
      code: 200,
      message: '获取成功',
      success: true,
      data: currentUser ? { ...currentUser, permissions: dynamicPermissions } : null,
      timestamp: new Date().toISOString(),
    });
  }),

  // Logout
  http.post('/api/auth/logout', () => {
    return HttpResponse.json({
      code: 200,
      message: '退出登录成功',
      success: true,
      data: null,
      timestamp: new Date().toISOString(),
    });
  }),

  // Change Password
  http.post('/api/auth/change-password', async ({ request }) => {
    const body = (await request.json()) as { oldPassword?: string; newPassword?: string; username?: string };
    const { oldPassword, newPassword } = body;

    if (!oldPassword || !newPassword) {
      return HttpResponse.json(
        {
          code: 400,
          message: '原密码与新密码均不能为空',
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 400 },
      );
    }

    if (newPassword.length < 6 || newPassword.length > 32) {
      return HttpResponse.json(
        {
          code: 400,
          message: '新密码长度必须在 6 至 32 位之间',
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 400 },
      );
    }

    if (oldPassword === newPassword) {
      return HttpResponse.json(
        {
          code: 400,
          message: '新密码不能与原密码相同',
          success: false,
          timestamp: new Date().toISOString(),
        },
        { status: 400 },
      );
    }

    // Determine target user from username in body or token
    let targetKey = 'admin';
    if (body.username && mockUsers[body.username]) {
      targetKey = body.username;
    } else {
      const foundKey = Object.keys(mockUsers).find((k) => mockUsers[k]?.user.username === body.username);
      if (foundKey) {
        targetKey = foundKey;
      }
    }

    const targetUserObj = mockUsers[targetKey] || Object.values(mockUsers).find((u) => u.user.username === body.username) || mockUsers.admin;

    if (targetUserObj) {
      if (targetUserObj.password !== oldPassword) {
        return HttpResponse.json(
          {
            code: 400,
            message: '原密码不正确，请重新输入',
            success: false,
            timestamp: new Date().toISOString(),
          },
          { status: 400 },
        );
      }
      targetUserObj.password = newPassword;
    }

    return HttpResponse.json({
      code: 200,
      message: '密码修改成功，请使用新密码重新登录',
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),
];
